import React, { useRef, useMemo, type FC } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';

import {
	ModalBody,
	ModalFooter,
	ModalHeader,
	ModalTitle,
	CloseButton,
} from '@atlaskit/modal-dialog';
import Button from '@atlaskit/button/new';
import { Inline, Stack, Text, Box } from '@atlaskit/primitives/compiled';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';
import { cssMap } from '@atlaskit/css';

import { fg } from '@confluence/feature-gating';
import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import { useGroupsAndTeamCountQuery } from '../controller/queries/useGroupsCountQuery.ts';
import { useShareDialogTeamDataHandler } from '../controller/queries/useShareDialogTeamDataHandler.ts';

import { GroupDetails } from './shareAndRestrictDialog/body/specificAccessDetails/specificAccessRow/GroupDetails.tsx';
import type { GroupOrTeamPrincipal } from './commonTypes.tsx';

const styles = cssMap({
	spinnerContainer: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		height: '100%',
		padding: token('space.200'),
	},
});

export type GroupNotifyWarningDialogProps = {
	groupNames: string[];
	groupList: GroupOrTeamPrincipal[];
	isSaving: boolean;
	onDoNotify: () => void;
	onDoNotNotify: () => void;
	onCloseComplete: () => void;
};

const i18n = defineMessages({
	heading: {
		id: 'share-and-restrict-dialog.group-notify-warning.heading',
		defaultMessage: 'Notify everyone in the group?',
		description:
			'Heading for a pop-up modal that asks a user to confirm sending notifications to all group members',
	},
	headingWithTeams: {
		id: 'share-and-restrict-dialog.group-notify-warning.heading-with-teams',
		defaultMessage: 'Notify everyone?',
		description:
			'Heading for a pop-up modal that asks a user to confirm sending notifications to all team and group members',
	},
	body: {
		id: 'share-and-restrict-dialog.group-notify-warning.body',
		defaultMessage:
			'Groups often contain a lot of people. It might be a little noisy to notify them all. Are you sure you want to notify everyone in these groups?',
		description: 'Body for a pop-up modal that interrupts sharing content to groups',
	},
	bodyWithTeams: {
		id: 'share-and-restrict-dialog.group-notify-warning.body-with-teams',
		defaultMessage:
			'Teams and groups often contain a lot of people. It might be a little noisy to notify them all. Are you sure you want to notify everyone in these teams and groups?',
		description: 'Body for a pop-up modal that interrupts sharing content to teams and groups',
	},
	doNotifyButton: {
		id: 'share-and-restrict-dialog.group-notify-warning.do-notify',
		defaultMessage: 'Notify groups',
		description:
			'The label for a button that will share a document and notify all new users, including those in groups',
	},
	doNotifyButtonWithTeams: {
		id: 'share-and-restrict-dialog.group-notify-warning.do-notify-with-teams',
		defaultMessage: 'Notify them all',
		description:
			'The label for a button that will share a document and notify all new users, including those in groups',
	},
	doNotNotifyButton: {
		id: 'share-and-restrict-dialog.group-notify-warning.do-not-notify',
		defaultMessage: 'Share without notifying',
		description:
			'The label for a button that will share a document, and notify individuals but not group members',
	},
});

export const GroupNotifyWarningDialog: FC<GroupNotifyWarningDialogProps> = ({
	groupNames,
	groupList,
	isSaving,
	onDoNotify,
	onDoNotNotify,
	onCloseComplete,
}) => {
	const focusRef = useRef<HTMLButtonElement>(null);
	const { Modal } = useChoreographerAssets();
	const { cloudId } = useSessionData();

	//This query is called here in order to retrieve the member counts for groups, since those are not accessible from the recommendations service
	//It's not the most performant solution, but it's the only way to get the member counts for groups in the GroupNotifyWarningDialog component
	//The best way to do this would be to have URS return group member counts. Until then, this is the best workaround.
	const { groupAndTeamCountsMap, isGroupsCountLoading } = useGroupsAndTeamCountQuery({
		data: undefined,
		publishModalRestrictions: undefined,
		error: undefined,
		groupList,
	});

	const { teamData, isTeamDataLoading } = useShareDialogTeamDataHandler(cloudId);

	const teamTypeNameMap = useMemo(() => {
		return teamData?.teamsV2?.map((team): { id: string; typeName: string } => {
			return {
				id: team?.id ?? '',
				typeName: team?.type?.name ?? '',
			};
		});
	}, [teamData]);

	// Enrich groupList with member counts and team type names
	const enrichedGroupList = useMemo(() => {
		return groupList.map((group) => {
			const enrichedGroup = { ...group };
			const count = groupAndTeamCountsMap.get(group.id);
			if (count !== undefined) {
				enrichedGroup.memberCount = count;
			}
			// Add teamTypeName for teams
			if (group.type === 'team' || group.usageType === 'TEAM_COLLABORATION') {
				const teamTypeName = teamTypeNameMap?.find((team) => team.id === group.id)?.typeName;
				if (teamTypeName) {
					enrichedGroup.teamTypeName = teamTypeName;
				}
			}
			return enrichedGroup;
		});
	}, [groupList, groupAndTeamCountsMap, teamTypeNameMap]);

	// Effectively disable the close button without it looking disabled
	const closeModal = () => !isSaving && onDoNotNotify();

	const isLoading = isGroupsCountLoading || isTeamDataLoading;

	return (
		<Modal
			width="medium"
			autoFocus={focusRef}
			onCloseComplete={onCloseComplete}
			testId="modal-dialog-share-group-notify-warning"
		>
			{isLoading ? (
				<Box xcss={styles.spinnerContainer}>
					<Spinner size="large" />
				</Box>
			) : (
				<>
					<ModalHeader>
						<ModalTitle>
							<FormattedMessage
								{...(fg('confluence_frontend_perms_exp_teams_as_principals')
									? i18n.headingWithTeams
									: i18n.heading)}
							/>
						</ModalTitle>
						<CloseButton onClick={closeModal} />
					</ModalHeader>
					<ModalBody>
						<Stack space="space.200">
							<FormattedMessage
								{...(fg('confluence_frontend_perms_exp_teams_as_principals')
									? i18n.bodyWithTeams
									: i18n.body)}
							/>
							{!fg('confluence_frontend_perms_exp_teams_as_principals') ? (
								<Inline>
									<Text weight="bold">{groupNames.join(', ')}</Text>
								</Inline>
							) : (
								<Stack space="space.200">
									{enrichedGroupList.map((group) => {
										return (
											<GroupDetails
												key={group.id}
												{...group}
												data-testid={`group-details-${group.type}`}
											/>
										);
									})}
								</Stack>
							)}
						</Stack>
					</ModalBody>
					<ModalFooter>
						<Button isDisabled={isSaving} onClick={onDoNotNotify}>
							<FormattedMessage {...i18n.doNotNotifyButton} />
						</Button>
						<Button appearance="primary" isDisabled={isSaving} onClick={onDoNotify} ref={focusRef}>
							<FormattedMessage
								{...(fg('confluence_frontend_perms_exp_teams_as_principals')
									? i18n.doNotifyButtonWithTeams
									: i18n.doNotifyButton)}
							/>
						</Button>
					</ModalFooter>
				</>
			)}
		</Modal>
	);
};
